import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { useNavigate } from 'react-router-dom'

import {
  answerCheckSelector,
  answerMapSelector,
  counterSelector,
  currentNumberSelector,
  currentQuestionSelector,
  finishQuiz,
  isAnswerSelector,
  isErrorSelector,
  isLoadingSelector,
  lengthSelector,
  setCounter,
  setNextQuestion,
  variantsSelector,
} from '@/services/slices/questionSlice'
import { AppDispatch } from '@/store'
import Button from '@/ui/Button/Button'
import Logo from '@/ui/icons/Logo'
import MapWrapper from '@/ui/MapWrapper/MapWrapper'
import { createResult, getAnswer } from '@/utils/api'

import styles from './styles.module.scss'

import AnswerCard from './componets/AnswerCard/AnswerCard'
import AnswerCardMobile from './componets/AnswerCard/mobile/AnswerCardMobile'
import QuestionCard from './componets/QuestionCard/QuestionCard'

const QuestionPage = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 })

  const questionNumber = useSelector(currentNumberSelector)
  const isAnswer = useSelector(isAnswerSelector)
  const isError = useSelector(isErrorSelector)
  const isLoading = useSelector(isLoadingSelector)
  const lastQuestionNumber = useSelector(lengthSelector)
  const checkedAnswer = useSelector(answerCheckSelector)
  const variantsArray = useSelector(variantsSelector)
  const mapConfig = useSelector(answerMapSelector)
  const corrects = useSelector(counterSelector)
  const currentQuestion = useSelector(currentQuestionSelector)

  const dispatch = useDispatch<AppDispatch>()

  const navigate = useNavigate()

  useEffect(() => {
    if (!Object.keys(currentQuestion).length) {
      navigate('/quiz')
    }
  }, [currentQuestion, navigate])

  const onAnswer = (id: number) => {
    dispatch(getAnswer(id))
  }

  const onNext = useCallback(() => {
    if (checkedAnswer) {
      dispatch(setCounter())
    }

    dispatch(setNextQuestion(questionNumber))
  }, [checkedAnswer, dispatch, questionNumber])

  const onLanding = () => {
    dispatch(finishQuiz())
  }

  const onResults = useCallback(async () => {
    dispatch(
      createResult({
        corrects: corrects + +checkedAnswer,
        questions: lastQuestionNumber,
      }),
    ).then(state => {
      navigate(`/results/${state.payload.id}`)
    })
  }, [checkedAnswer, corrects, dispatch, lastQuestionNumber, navigate])

  return (
    <div>
      {isAnswer && !isLoading && !isError ? (
        <div className={styles.answer}>
          <div className={styles.answer_map}>
            <MapWrapper
              map={{
                ...(isMobile && mapConfig.centerMobile
                  ? {
                      ...(mapConfig.centerMobile && {
                        center: mapConfig.centerMobile,
                      }),
                    }
                  : {
                      ...(mapConfig.center && { center: mapConfig.center }),
                    }),
                ...(isMobile && mapConfig.zoomMobile
                  ? {
                      ...(mapConfig.zoomMobile && {
                        zoom: mapConfig.zoomMobile,
                      }),
                    }
                  : {
                      ...(mapConfig.zoom && { zoom: mapConfig.zoom }),
                    }),
                ...(mapConfig.pitch && { pitch: mapConfig.pitch }),
                ...(mapConfig.rotation && { rotation: mapConfig.rotation }),
              }}
              markerCords={mapConfig.markerCords}
              setSelectedObjects={mapConfig.objects}
            />
          </div>
          {isMobile ? (
            <AnswerCardMobile />
          ) : (
            <div className={styles.container}>
              <AnswerCard />
              <div className={styles.container_buttons}>
                <Button
                  type="primary"
                  size="medium"
                  linkTo="/#records"
                  onClick={onLanding}
                  className={styles.answer_button}
                >
                  <span>Завершить игру</span>
                </Button>
                <Button
                  type="accent"
                  size="medium"
                  className={styles.answer_button}
                  onClick={
                    questionNumber === lastQuestionNumber ? onResults : onNext
                  }
                >
                  <span>
                    {questionNumber === lastQuestionNumber
                      ? 'Посмотреть результат'
                      : 'Следующий вопрос'}
                  </span>
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.question}>
          <div className={styles.container}>
            {!isMobile && (
              <a
                href="https://2gis.ru"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Logo />
              </a>
            )}
            <QuestionCard />
            <div className={styles.buttons_wrapper}>
              {(variantsArray || []).map(
                (item: { id: number; name: string }) => (
                  <Button
                    key={item.id}
                    type="primary"
                    size={isMobile ? 'fullsize' : 'large'}
                    onClick={() => onAnswer(item.id)}
                  >
                    {item.name}
                  </Button>
                ),
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default QuestionPage
